/**
 * Skeleton - Loading placeholder components
 *
 * Provides skeleton loading states that match the shape of content:
 * - Base Skeleton component for custom shapes
 * - Pre-built variants for cards, lists, etc.
 */

import { CSSProperties, ReactNode } from 'react';

export interface SkeletonProps {
  width?: number | string;
  height?: number | string;
  borderRadius?: number | string;
  style?: CSSProperties;
  className?: string;
}

export function Skeleton({
  width = '100%',
  height = 20,
  borderRadius = 'var(--radius-sm)',
  style,
  className,
}: SkeletonProps) {
  return (
    <div
      className={`skeleton ${className || ''}`}
      aria-hidden="true"
      style={{
        width,
        height,
        borderRadius,
        background: 'linear-gradient(90deg, var(--gray-200) 25%, var(--gray-100) 50%, var(--gray-200) 75%)',
        backgroundSize: '200% 100%',
        animation: 'shimmer 1.5s infinite',
        ...style,
      }}
    />
  );
}

// Container for skeleton groups
interface SkeletonGroupProps {
  children: ReactNode;
  gap?: number;
  direction?: 'row' | 'column';
}

function SkeletonGroup({ children, gap = 12, direction = 'column' }: SkeletonGroupProps) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: direction,
        gap,
      }}
      aria-busy="true"
      aria-label="Loading content"
    >
      {children}
    </div>
  );
}

// Pre-built skeleton variants
interface CountProps {
  count?: number;
}

// Generic card skeleton
Skeleton.Card = function CardSkeleton({ count = 1 }: CountProps) {
  return (
    <SkeletonGroup direction="column" gap={16}>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          style={{
            background: 'white',
            borderRadius: 'var(--radius-md)',
            padding: 20,
            border: '1px solid var(--gray-200)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <Skeleton width={40} height={40} borderRadius="50%" />
            <div style={{ flex: 1 }}>
              <Skeleton height={16} width="60%" style={{ marginBottom: 6 }} />
              <Skeleton height={12} width="40%" />
            </div>
          </div>
          <Skeleton height={14} style={{ marginBottom: 8 }} />
          <Skeleton height={14} width="85%" />
        </div>
      ))}
    </SkeletonGroup>
  );
};

// Video card skeleton
Skeleton.VideoCard = function VideoCardSkeleton({ count = 1 }: CountProps) {
  return (
    <SkeletonGroup direction="column" gap={16}>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          style={{
            background: 'white',
            borderRadius: 'var(--radius-md)',
            overflow: 'hidden',
            border: '1px solid var(--gray-200)',
          }}
        >
          <Skeleton height={180} borderRadius={0} />
          <div style={{ padding: 16 }}>
            <Skeleton height={20} width="80%" style={{ marginBottom: 8 }} />
            <Skeleton height={14} width="50%" style={{ marginBottom: 12 }} />
            <div style={{ display: 'flex', gap: 8 }}>
              <Skeleton height={24} width={60} borderRadius={12} />
              <Skeleton height={24} width={80} borderRadius={12} />
            </div>
          </div>
        </div>
      ))}
    </SkeletonGroup>
  );
};

// List item skeleton
Skeleton.ListItem = function ListItemSkeleton({ count = 1 }: CountProps) {
  return (
    <SkeletonGroup direction="column" gap={0}>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 16px',
            background: 'white',
            borderBottom: '1px solid var(--gray-100)',
          }}
        >
          <Skeleton width={36} height={36} borderRadius="50%" />
          <div style={{ flex: 1 }}>
            <Skeleton height={14} width="50%" style={{ marginBottom: 6 }} />
            <Skeleton height={12} width="30%" />
          </div>
          <Skeleton width={80} height={28} borderRadius="var(--radius-sm)" />
        </div>
      ))}
    </SkeletonGroup>
  );
};

// Stats card skeleton
Skeleton.Stats = function StatsSkeleton({ count = 4 }: CountProps) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
        gap: 16,
      }}
      aria-busy="true"
      aria-label="Loading stats"
    >
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          style={{
            background: 'white',
            borderRadius: 'var(--radius-md)',
            padding: 20,
            border: '1px solid var(--gray-200)',
          }}
        >
          <Skeleton height={12} width={80} style={{ marginBottom: 12 }} />
          <Skeleton height={32} width={100} style={{ marginBottom: 8 }} />
          <Skeleton height={10} width={60} />
        </div>
      ))}
    </div>
  );
};

// Challenge card skeleton
Skeleton.ChallengeCard = function ChallengeCardSkeleton({ count = 1 }: CountProps) {
  return (
    <SkeletonGroup direction="column" gap={12}>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: 16,
            background: 'white',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--gray-200)',
          }}
        >
          <Skeleton width={44} height={44} borderRadius="var(--radius-sm)" />
          <div style={{ flex: 1 }}>
            <Skeleton height={16} width="50%" style={{ marginBottom: 6 }} />
            <Skeleton height={12} width="35%" />
          </div>
          <Skeleton width={60} height={24} borderRadius={12} />
        </div>
      ))}
    </SkeletonGroup>
  );
};

// Team card skeleton
Skeleton.TeamCard = function TeamCardSkeleton({ count = 1 }: CountProps) {
  return (
    <SkeletonGroup direction="column" gap={12}>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: 16,
            background: 'white',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--gray-200)',
          }}
        >
          <Skeleton width={40} height={40} borderRadius="50%" />
          <div style={{ flex: 1 }}>
            <Skeleton height={16} width="40%" style={{ marginBottom: 6 }} />
            <Skeleton height={12} width="25%" />
          </div>
          <Skeleton width={24} height={24} borderRadius="var(--radius-sm)" />
        </div>
      ))}
    </SkeletonGroup>
  );
};

export default Skeleton;
